// Focus trap for modal-like containers.
// On mount: remembers the element that previously had focus, then focuses the
// first focusable child of `node`. On Tab/Shift+Tab, cycles within the trap.
// On destroy: restores focus to the previously-focused element.

const FOCUSABLE_SELECTOR = [
	'a[href]',
	'button:not([disabled])',
	'input:not([disabled]):not([type="hidden"])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	// <summary> est nativement tabbable : sans lui, Tab sort du piège quand un
	// <details> replié termine le dialogue.
	'summary',
	'[tabindex]:not([tabindex="-1"])'
].join(',');

function getFocusable(node: HTMLElement): HTMLElement[] {
	return Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
		(el) => el.offsetParent !== null || el === document.activeElement
	);
}

export function trapFocus(node: HTMLElement) {
	const previouslyFocused = document.activeElement as HTMLElement | null;

	function focusFirst() {
		const focusable = getFocusable(node);
		if (focusable.length === 0) {
			node.tabIndex = -1;
			node.focus();
		} else {
			focusable[0].focus();
		}
	}

	function handleKeydown(e: KeyboardEvent) {
		if (e.key !== 'Tab') return;
		const focusable = getFocusable(node);
		if (focusable.length === 0) {
			e.preventDefault();
			return;
		}
		const first = focusable[0];
		const last = focusable[focusable.length - 1];
		const active = document.activeElement as HTMLElement | null;
		if (e.shiftKey) {
			if (active === first || !node.contains(active)) {
				e.preventDefault();
				last.focus();
			}
		} else {
			if (active === last || !node.contains(active)) {
				e.preventDefault();
				first.focus();
			}
		}
	}

	// Defer initial focus until after Svelte mounts the children.
	queueMicrotask(focusFirst);
	node.addEventListener('keydown', handleKeydown);

	return {
		destroy() {
			node.removeEventListener('keydown', handleKeydown);
			previouslyFocused?.focus?.();
		}
	};
}
